import React, {
  useCallback, useEffect, useState, useMemo, useRef,
} from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Redirect,
  useParams,
  useLocation,
  useHistory,
} from 'react-router-dom';
import { useIntl } from 'react-intl';
import { Button, Container } from 'reactstrap';

import {
  APIgetPage,
  APIgetPageCollectionDetail,
  APIgetPageCollectionFiles,
} from '../api/Pages';
import { isLoggedIn } from '../api/Auth';
import { cleanLoginParams } from '../helpers/App';
import { getSetting } from '../helpers/Settings';
import { showModal } from '../actions';
import { getCollectionAllowedViews, getLoadedCollectionsLabel } from '../helpers/Collections';
import PageHeader from '../components/Page/Header';
import PageFooter from '../components/Page/Footer';
import Loading from '../components/Loading';
import { PageIdContext } from '../helpers/PageIdContext';
import { getPageFileRoute, getTransparentHeaderPaddingClass } from '../helpers/PageSections';
import FilesMasonry from '../components/Files/Masonry';
import FilesGrid from '../components/Files/Grid';
import FilesTile from '../components/Files/Tile';
import CollectionsGrid from '../components/Collections/Grid';
import FilesSelectAll from '../components/Files/SelectAll';
import CollectionsSelectAll from '../components/Collections/SelectAll';
import { renderPageCollectionAction } from '../helpers/PageElementActions';
import Actionbar from '../components/Actionbar';
import ViewMode from '../components/Filters/ViewMode';
import {
  getCollection,
  addSelectedCollections,
  setSelectedAllCollections,
} from '../actions/Collections';
import { addSelectedFiles, setSelectedAllFiles } from '../actions/Files';
import { getLoadedFilesLabel } from '../helpers/Files';
import { handleURLParams } from '../helpers/URLParams';

const FILES_PER_PAGE = 36;
const COLLECTIONS_PER_PAGE = 24;
const pageCollectionViewCache = new Map();

const PageCollectionView = ({
  onShowModal,
  handleGetCollection,
  selectLoadedFiles,
  selectLoadedCollections,
  resetSelectAll,
  fixedModal,
}) => {
  const intl = useIntl();
  const { pageId, collectionUuid } = useParams();
  const location = useLocation();
  const history = useHistory();

  const { pathname, search, state: locationState } = location;
  const paramView = new URLSearchParams(search).get('view');
  const safeCollectionUuid = (collectionUuid || '').split('?')[0];
  const pageBasePath = pathname.startsWith('/pages/') ? 'pages' : 'content';

  const [page, setPage] = useState(null);
  const [collectionMeta, setCollectionMeta] = useState(null);
  const [childCollections, setChildCollections] = useState([]);
  const [childCollectionsTotal, setChildCollectionsTotal] = useState(0);
  const [filesTotal, setFilesTotal] = useState(0);
  const [files, setFiles] = useState([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [collectionsLoading, setCollectionsLoading] = useState(true);
  const [filesLoading, setFilesLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filesPage, setFilesPage] = useState(1);
  const [hasMoreFiles, setHasMoreFiles] = useState(false);
  const [collectionsPage, setCollectionsPage] = useState(1);
  const [hasMoreCollections, setHasMoreCollections] = useState(false);
  const [redirectToLogin, setRedirectToLogin] = useState(false);
  const [collectionsExpanded, setCollectionsExpanded] = useState(true);
  const [collectionsView, setCollectionsView] = useState(['grid', 'framed'].includes(paramView) ? paramView : 'grid');
  const [filesView, setFilesView] = useState(paramView || 'grid');

  useEffect(() => {
    setFilesView(paramView || 'grid');
  }, [paramView]);
  const [filesExpanded, setFilesExpanded] = useState(true);

  const modalScrollRef = useRef(null);
  const isModalView = !!fixedModal;
  const cacheKey = useMemo(
    () => `${pageBasePath}:${pageId}:${safeCollectionUuid}`,
    [pageBasePath, pageId, safeCollectionUuid],
  );
  const filesRequestContext = JSON.stringify([cacheKey, filesView]);
  const filesRequestContextRef = useRef(filesRequestContext);
  filesRequestContextRef.current = filesRequestContext;
  const collectionsRequestContext = JSON.stringify([cacheKey, collectionsView]);
  const collectionsRequestContextRef = useRef(collectionsRequestContext);
  collectionsRequestContextRef.current = collectionsRequestContext;

  const loading = pageLoading || (!collectionMeta && collectionsLoading);

  useEffect(() => {
    if (!isModalView || typeof document === 'undefined') {
      return undefined;
    }

    const className = 'page-collection-modal-open';
    document.documentElement.classList.add(className);
    document.body.classList.add(className);

    return () => {
      document.documentElement.classList.remove(className);
      document.body.classList.remove(className);
    };
  }, [isModalView]);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    let cancelled = false;
    const cachedState = pageCollectionViewCache.get(cacheKey);

    resetSelectAll();
    setPage(cachedState?.page || null);
    setCollectionMeta(cachedState?.collectionMeta || null);
    handleGetCollection(cachedState?.collectionMeta || null);
    setChildCollections(cachedState?.childCollections || []);
    setChildCollectionsTotal(cachedState?.childCollectionsTotal || 0);
    setFiles(cachedState?.files || []);
    setFilesPage(cachedState?.filesPage || 1);
    setFilesTotal(cachedState?.filesTotal || 0);
    setCollectionsPage(cachedState?.collectionsPage || 1);
    setHasMoreFiles(!!cachedState?.hasMoreFiles);
    setHasMoreCollections(!!cachedState?.hasMoreCollections);
    setError(null);
    setRedirectToLogin(false);
    setPageLoading(!cachedState?.page);

    APIgetPage(pageId)
      .then(({ data }) => {
        if (cancelled) return;
        setPage(data);
        setPageLoading(false);
        pageCollectionViewCache.set(cacheKey, {
          ...pageCollectionViewCache.get(cacheKey),
          page: data,
        });
      })
      .catch((requestError) => {
        if (cancelled) return;
        setPageLoading(false);
        if (requestError.response?.status === 403 && requestError.response?.data?.error === 'login_required') {
          setRedirectToLogin(true);
          return;
        }
        setError(intl.formatMessage({ defaultMessage: 'Failed to load collection.' }));
      });

    return () => {
      cancelled = true;
      resetSelectAll();
    };
  }, [cacheKey, pageId, safeCollectionUuid]);

  useEffect(() => {
    let cancelled = false;
    setCollectionsLoading(true);
    APIgetPageCollectionDetail(pageId, safeCollectionUuid, {
      page: 1,
      per_page: COLLECTIONS_PER_PAGE,
      view: collectionsView,
    })
      .then(({ data }) => {
        if (cancelled) return;
        const nextCollectionMeta = data?.collection ?? null;
        const nextChildCollections = data?.child_collections ?? [];
        const nextTotal = data?.meta?.total ?? 0;
        const nextHasMore = !!data?.meta?.has_more;
        setCollectionMeta(nextCollectionMeta);
        handleGetCollection(nextCollectionMeta);
        setChildCollections(nextChildCollections);
        selectLoadedCollections(nextChildCollections);
        setChildCollectionsTotal(nextTotal);
        setCollectionsPage(1);
        setHasMoreCollections(nextHasMore);
        setCollectionsLoading(false);
        pageCollectionViewCache.set(cacheKey, {
          ...pageCollectionViewCache.get(cacheKey),
          collectionMeta: nextCollectionMeta,
          childCollections: nextChildCollections,
          childCollectionsTotal: nextTotal,
          collectionsPage: 1,
          hasMoreCollections: nextHasMore,
        });
      })
      .catch((requestError) => {
        if (cancelled) return;
        setCollectionsLoading(false);
        if (requestError.response?.status === 403 && requestError.response?.data?.error === 'login_required') {
          setRedirectToLogin(true);
          return;
        }
        setError(intl.formatMessage({ defaultMessage: 'Failed to load collection.' }));
      });

    return () => { cancelled = true; };
  }, [cacheKey, pageId, safeCollectionUuid, collectionsView]);

  useEffect(() => {
    let cancelled = false;
    setFilesLoading(true);
    APIgetPageCollectionFiles(pageId, safeCollectionUuid, {
      page: 1,
      per_page: FILES_PER_PAGE,
      view: filesView,
    })
      .then(({ data }) => {
        if (cancelled) return;
        const nextFiles = data?.data?.data ?? data?.data ?? [];
        const nextTotal = data?.data?.meta?.total ?? data?.meta?.total ?? 0;
        const nextHasMore = !!(data?.data?.meta?.has_more ?? data?.meta?.has_more);
        setFiles(nextFiles);
        selectLoadedFiles(nextFiles.filter(file => file.urls));
        setFilesTotal(nextTotal);
        setFilesPage(1);
        setHasMoreFiles(nextHasMore);
        setFilesLoading(false);
        pageCollectionViewCache.set(cacheKey, {
          ...pageCollectionViewCache.get(cacheKey),
          files: nextFiles,
          filesTotal: nextTotal,
          filesPage: 1,
          hasMoreFiles: nextHasMore,
        });
      })
      .catch((requestError) => {
        if (cancelled) return;
        setFilesLoading(false);
        if (requestError.response?.status === 403 && requestError.response?.data?.error === 'login_required') {
          setRedirectToLogin(true);
          return;
        }
        setError(intl.formatMessage({ defaultMessage: 'Failed to load collection.' }));
      });

    return () => { cancelled = true; };
  }, [cacheKey, pageId, safeCollectionUuid, filesView]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const isUserLoggedIn = isLoggedIn();
  const cs1Class = getSetting('cs1') ? ' cs1' : '';
  const headerSearchClass = getTransparentHeaderPaddingClass(
    page?.options?.header,
    false,
    page?.options?.showSearchBar ?? page?.options?.show_search_bar ?? true,
  );

  const resolvedPageId = page?.id || pageId;
  const resolvedPageRouteId = page?.url || pageId;
  const canDownloadCollection = !!(collectionMeta?.allow_download);
  const hasCollectionData = !!collectionMeta || childCollections.length > 0 || files.length > 0;
  const defaultBackPath = `/${pageBasePath}/${resolvedPageRouteId}`;
  const currentUrl = `${pathname}${search || ''}`;
  const incomingBackTrail = useMemo(() => (
    Array.isArray(locationState?.backTrail)
      ? locationState.backTrail.filter(Boolean)
      : []
  ), [locationState]);
  const backLinkTarget = incomingBackTrail.length > 0
    ? incomingBackTrail[incomingBackTrail.length - 1]
    : defaultBackPath;

  const getLocationFromUrl = useCallback((url, state = undefined) => {
    const [targetPathname, targetSearch = ''] = String(url || '').split('?');
    return {
      pathname: targetPathname,
      search: targetSearch ? `?${targetSearch}` : '',
      state,
    };
  }, []);

  const handleGoBack = useCallback((event) => {
    if (event) {
      event.preventDefault();
    }

    if (incomingBackTrail.length > 0) {
      const target = incomingBackTrail[incomingBackTrail.length - 1];
      const nextTrail = incomingBackTrail.slice(0, -1);
      history.replace(getLocationFromUrl(target, {
        backTrail: nextTrail,
      }));
      return;
    }

    // Default return is page when no back trail is provided
    history.replace(defaultBackPath);
  }, [
    defaultBackPath,
    getLocationFromUrl,
    history,
    incomingBackTrail,
  ]);

  const handleDownloadCollection = () => {
    if (!collectionMeta) {
      return;
    }

    onShowModal('DownloadModal', {
      className: 'modal-download',
      files: [],
      collections: [collectionMeta],
      pageId: resolvedPageId,
      backdrop: true,
    });
  };

  const renderDisplayAction = useCallback((targetFile, actionView) => {
    if (!canDownloadCollection || !resolvedPageId || !targetFile?.uuid) {
      return null;
    }

    const label = intl.formatMessage({ defaultMessage: 'Download' });
    const actionId = `PageCollectionViewDownload-${targetFile.uuid}-${actionView || filesView || 'grid'}`;

    return (
      <li>
        <a
          href="#download"
          id={actionId}
          aria-label={label}
          title={label}
          onClick={(e) => {
            e.preventDefault();
            onShowModal('DownloadModal', {
              className: 'modal-download',
              files: [targetFile],
              collections: [],
              fileCollectionMap: safeCollectionUuid ? {
                [targetFile.uuid]: [safeCollectionUuid],
              } : {},
              pageId: resolvedPageId,
              backdrop: true,
            });
          }}
        >
          <i className="far fa-download" aria-hidden="true" />
        </a>
      </li>
    );
  }, [canDownloadCollection, intl, onShowModal, resolvedPageId, filesView, safeCollectionUuid]);

  const renderChildCollectionDisplayAction = useCallback(targetCollection => (
    renderPageCollectionAction({
      collection: targetCollection,
      pageId: resolvedPageId,
      onShowModal,
      intl,
    })
  ), [intl, onShowModal, resolvedPageId]);

  const header = (
    <PageHeader
      headerType={page?.options?.header}
      pageLogoUrl={page?.options?.logo_url_preview}
      showSearchBar={page?.options?.showSearchBar ?? page?.options?.show_search_bar ?? true}
      pageNavigation={page?.options?.pageNavigation || page?.options?.page_navigation || []}
      pageBasePath={pageBasePath}
      pageRouteId={pageId}
      pageId={page?.id || pageId}
      pageVisibilityMode={page?.visibility_mode || page?.options?.visibility_mode || null}
      filesView={filesView}
    />
  );

  const fetchMoreFiles = useCallback(() => {
    if (!hasMoreFiles || filesLoading) return;

    setFilesLoading(true);
    APIgetPageCollectionFiles(
      pageId,
      safeCollectionUuid,
      { page: filesPage + 1, per_page: FILES_PER_PAGE, view: filesView },
    )
      .then((res) => {
        if (filesRequestContextRef.current !== filesRequestContext) return;

        const newFiles = res.data?.data?.data ?? res.data?.data ?? [];
        const nextHasMoreFiles = !!(
          res.data?.data?.meta?.has_more
          ?? res.data?.meta?.has_more
        );

        selectLoadedFiles(newFiles.filter(file => file.urls));
        setFiles((prev) => {
          const merged = [...prev, ...newFiles];
          const existing = pageCollectionViewCache.get(cacheKey) || {};
          pageCollectionViewCache.set(cacheKey, {
            ...existing,
            files: merged,
            filesPage: filesPage + 1,
            hasMoreFiles: nextHasMoreFiles,
          });
          return merged;
        });
        setFilesPage((prev) => prev + 1);
        setHasMoreFiles(nextHasMoreFiles);
        setFilesLoading(false);
      })
      .catch(() => {
        if (filesRequestContextRef.current === filesRequestContext) {
          setFilesLoading(false);
        }
      });
  }, [
    hasMoreFiles,
    filesLoading,
    pageId,
    safeCollectionUuid,
    filesPage,
    filesView,
    cacheKey,
    filesRequestContext,
    selectLoadedFiles,
  ]);

  const fetchMoreCollections = useCallback(() => {
    if (!hasMoreCollections || collectionsLoading) return;

    setCollectionsLoading(true);
    APIgetPageCollectionDetail(
      pageId,
      safeCollectionUuid,
      { page: collectionsPage + 1, per_page: COLLECTIONS_PER_PAGE, view: collectionsView },
    )
      .then((res) => {
        if (collectionsRequestContextRef.current !== collectionsRequestContext) return;

        const newCollections = res.data?.child_collections ?? [];
        const nextHasMoreCollections = !!res.data?.meta?.has_more;

        selectLoadedCollections(newCollections);
        setChildCollections((prev) => {
          const merged = [...prev, ...newCollections];
          const existing = pageCollectionViewCache.get(cacheKey) || {};
          pageCollectionViewCache.set(cacheKey, {
            ...existing,
            childCollections: merged,
            collectionsPage: collectionsPage + 1,
            hasMoreCollections: nextHasMoreCollections,
          });
          return merged;
        });
        setCollectionsPage((prev) => prev + 1);
        setHasMoreCollections(nextHasMoreCollections);
        setCollectionsLoading(false);
      })
      .catch(() => {
        if (collectionsRequestContextRef.current === collectionsRequestContext) {
          setCollectionsLoading(false);
        }
      });
  }, [
    hasMoreCollections,
    collectionsLoading,
    pageId,
    safeCollectionUuid,
    collectionsPage,
    collectionsView,
    cacheKey,
    collectionsRequestContext,
    selectLoadedCollections,
  ]);

  useEffect(() => {
    if (isModalView) {
      const scrollEl = modalScrollRef.current;
      if (!scrollEl) {
        return undefined;
      }

      const handleModalScroll = () => {
        if (scrollEl.scrollTop + scrollEl.clientHeight >= scrollEl.scrollHeight - 100) {
          fetchMoreFiles();
          fetchMoreCollections();
        }
      };

      scrollEl.addEventListener('scroll', handleModalScroll);
      return () => scrollEl.removeEventListener('scroll', handleModalScroll);
    }

    const handleWindowScroll = () => {
      if (
        window.innerHeight + document.documentElement.scrollTop >=
        document.documentElement.offsetHeight - 100
      ) {
        fetchMoreFiles();
        fetchMoreCollections();
      }
    };

    window.addEventListener('scroll', handleWindowScroll);
    return () => window.removeEventListener('scroll', handleWindowScroll);
  }, [fetchMoreCollections, fetchMoreFiles, isModalView]);

  const pageContextValue = useMemo(
    () => ({
      pageId: resolvedPageId,
      pageRouteId: resolvedPageRouteId,
      isEditing: false,
      pageBasePath,
      collection: collectionMeta,
      collectionFileUuids: files.map(file => file.uuid),
      visibilityMode: page?.visibility_mode || page?.options?.visibility_mode || null,
    }),
    [resolvedPageId, resolvedPageRouteId, pageBasePath, page, collectionMeta, files],
  );

  const renderChildCollections = () => {
    if (collectionsLoading && childCollections.length === 0) {
      return <Loading />;
    }

    if (!Array.isArray(childCollections) || childCollections.length === 0) {
      return null;
    }

    return (
      <div className="widget-files-big-list puck-feed-grid-wrapper puck-collection-view-grid-wrapper">
        <div className="collection-tools">
          <div className="list-switch">
            <a
              href="#collections-switch"
              onClick={() => setCollectionsExpanded(prev => !prev)}
            >
              {intl.formatMessage({ defaultMessage: 'Collections {label}' }, { label: getLoadedCollectionsLabel(childCollections, childCollectionsTotal) })}{' '}
              {collectionsExpanded ? (
                <i className="far fa-chevron-down" />
              ) : (
                <i className="far fa-chevron-right" />
              )}
            </a>
          </div>
          {collectionsExpanded && (
            <div className="collection-tools-actions">
              <div className="collection-tools-actions-buttons">
                {/* <Sort
              orderby={orderby}
              order={order}
              search={search}
              setOrder={setOrder}
              hideSave={hideSaveSort}
              collection={collection}
              size="sm"
            /> */}
                <ViewMode
                  view={collectionsView}
                  setView={(view) => {
                    if (view === collectionsView) return;
                    setChildCollections([]);
                    setCollectionsLoading(true);
                    setCollectionsView(view);
                  }}
                  size="sm"
                  allowedViews={getCollectionAllowedViews().filter(v => v !== 'list')}
                />
              </div>
              <CollectionsSelectAll
                collections={childCollections}
                collectionsTotal={childCollectionsTotal}
                share={null}
              />
            </div>
          )}
        </div>
        {collectionsExpanded && (
          <>
            <CollectionsGrid
              collections={childCollections}
              canDrag={false}
              allowCheck={true}
              displayOnly={true}
              renderDisplayAction={renderChildCollectionDisplayAction}
              className="puck-page-collection-children-grid"
              collectionUrlResolver={collection => ({
                pathname: `/${pageBasePath}/${resolvedPageRouteId}/collection/${collection.uuid}`,
                state: {
                  backTrail: incomingBackTrail[incomingBackTrail.length - 1] === currentUrl
                    ? incomingBackTrail
                    : [...incomingBackTrail, currentUrl],
                },
              })}
              selectedView={collectionsView}
            />
            {collectionsLoading && <Loading />}
          </>
        )}
      </div>
    );
  };

  if (redirectToLogin) {
    return (
      <Redirect
        to={{
          pathname: '/login',
          search,
          state: {
            from: `${pathname}${cleanLoginParams(search)}`,
          },
        }}
      />
    );
  }

  if (loading) {
    if (isModalView) {
      return (
        <div className="page-collection-view-modal landing-content">
          <div ref={modalScrollRef} className="page-collection-view-modal-scroll">
            <Container className="puck-collection-view mt-4 mb-4">
              <Loading />
            </Container>
          </div>
        </div>
      );
    }

    return (
      <div className={`layout layout-landing${cs1Class}`}>
        <div className={`landing-content${cs1Class} page-header-${page?.options?.header}${headerSearchClass}`}><Loading /></div>
        <PageFooter />
      </div>
    );
  }

  if (error || !hasCollectionData) {
    if (isModalView) {
      return (
        <PageIdContext.Provider value={pageContextValue}>
          <div className="page-collection-view-modal landing-content">
            <div ref={modalScrollRef} className="page-collection-view-modal-scroll">
              {header}
              <Container className="puck-collection-view mt-4 mb-4">
                <p className="mt-4">{error || intl.formatMessage({ defaultMessage: 'Collection not found.' })}</p>
                <a href={backLinkTarget} onClick={handleGoBack} className="puck-collection-view-back">
                  <i className="far fa-arrow-left" />
                  {intl.formatMessage({ defaultMessage: 'Back' })}
                </a>
              </Container>
            </div>
          </div>
        </PageIdContext.Provider>
      );
    }

    return (
      <div className={`layout layout-landing${cs1Class}`}>
        {isUserLoggedIn && header}
        <div className={`landing-content${cs1Class} page-header-${page?.options?.header}${headerSearchClass}`}>
          <Container>
            <p className="mt-4">{error || intl.formatMessage({ defaultMessage: 'Collection not found.' })}</p>
            <a href={backLinkTarget} onClick={handleGoBack}>
              <i className="far fa-arrow-left" />
              {intl.formatMessage({ defaultMessage: 'Back' })}
            </a>
          </Container>
        </div>
        <PageFooter />
        <Actionbar />
      </div>
    );
  }

  const visibleFiles = Array.isArray(files)
    ? files
      .filter(file => file.urls)
      .map(file => ({
        ...file,
        to: (() => {
          const route = getPageFileRoute({
            pageBasePath,
            pageRouteId: resolvedPageRouteId,
            pageId: resolvedPageId,
            file,
          });

          if (!route) {
            return null;
          }

          return {
            ...route,
            state: {
              ...(route.state || {}),
              backUrl: `${pathname}${search}`,
              backUrlState: {
                backTrail: incomingBackTrail,
              },
              pageCollectionAllowDownload: canDownloadCollection,
              pageCollectionUuid: safeCollectionUuid || null,
            },
          };
        })(),
      }))
    : [];

  const renderFiles = () => {
    if (visibleFiles.length === 0) {
      if (filesLoading) {
        return <Loading />;
      }
      return null;
    }

    return (
      <div className="widget-files-big-list puck-feed-grid-wrapper puck-collection-view-grid-wrapper">
        <div className="collection-tools">
          <div className="list-switch">
            <a
              href="#files-switch"
              onClick={() => setFilesExpanded(prev => !prev)}
            >
              {intl.formatMessage({ defaultMessage: 'Files {label}' }, { label: getLoadedFilesLabel(visibleFiles, filesTotal) })}{' '}
              {filesExpanded ? (
                <i className="far fa-chevron-down" />
              ) : (
                <i className="far fa-chevron-right" />
              )}
            </a>
          </div>
          {filesExpanded && (
          <div className="collection-tools-actions">
            <div className="collection-tools-actions-buttons">
              {/* <Sort
                orderby={orderby}
                order={order}
                search={search}
                setOrder={setOrder}
                hideSave={hideSaveSort}
                collection={collection}
                size="sm"
              /> */}
              <ViewMode
                view={filesView}
                setView={(view) => {
                  if (view === filesView) return;
                  setFiles([]);
                  setFilesLoading(true);
                  setFilesView(view);
                  handleURLParams({ view }, history, locationState);
                }}
                size="sm"
                allowedViews={['grid', 'framed', 'masonry', 'tile']}
              />
            </div>
            <FilesSelectAll
              files={visibleFiles}
              filesTotal={filesTotal}
              share={null}
            />
          </div>
          )}
        </div>
        {filesExpanded && (
        <>
          {['grid', 'framed'].includes(filesView) && (
          <FilesGrid
            files={visibleFiles}
            canDrag={false}
            allowCheck={true}
            displayOnly={true}
            renderDisplayAction={renderDisplayAction}
            className="puck-feed-system-grid"
            selectedView={filesView}
          />
          )}
          {filesView === 'masonry' && (
          <FilesMasonry
            files={visibleFiles}
            displayOnly={true}
            allowCheck={true}
            renderDisplayAction={renderDisplayAction}
          />
          )}
          {filesView === 'tile' && (
          <FilesTile
            files={visibleFiles}
            displayOnly={true}
            allowCheck={true}
            disableRowConstraints={true}
            renderDisplayAction={renderDisplayAction}
          />
          )}
          {filesLoading && <Loading />}
        </>
        )}
      </div>
    );
  };

  return (
    <PageIdContext.Provider value={pageContextValue}>
      {isModalView ? (
        <div className="page-collection-view-modal landing-content">
          <div ref={modalScrollRef} className="page-collection-view-modal-scroll">
            {header}
            <Container className="puck-collection-view mt-4 mb-4">
              <div className="puck-collection-view-header">
                <a href={backLinkTarget} onClick={handleGoBack} className="puck-collection-view-back">
                  <i className="far fa-arrow-left" aria-hidden="true" />
                  <span>{intl.formatMessage({ defaultMessage: 'Back' })}</span>
                </a>
                <div className="puck-collection-view-header-main">
                  <h1 className="puck-section-title">
                    <span>{collectionMeta?.name || intl.formatMessage({ defaultMessage: 'Unnamed Collection' })}</span>
                  </h1>
                  {canDownloadCollection && (
                    <Button
                      color="primary"
                      className="puck-collection-view-download"
                      onClick={handleDownloadCollection}
                    >
                      <i className="far fa-download" /> {intl.formatMessage({ defaultMessage: 'Download all' })}
                    </Button>
                  )}
                </div>
              </div>
              {renderChildCollections()}
              {(filesLoading || visibleFiles.length > 0) && renderFiles()}
            </Container>
          </div>
          <Actionbar filesTotal={filesTotal} collectionsTotal={childCollectionsTotal} />
        </div>
      ) : (
        <div className={`layout layout-landing${cs1Class}`}>
          {header}
          <div className={`landing-content${cs1Class} page-header-${page?.options?.header}${headerSearchClass}`}>
            <Container className="puck-collection-view mt-4 mb-4">
              <div className="puck-collection-view-header">
                <a href={backLinkTarget} onClick={handleGoBack} className="puck-collection-view-back">
                  <i className="far fa-arrow-left" aria-hidden="true" />
                  <span>{intl.formatMessage({ defaultMessage: 'Back' })}</span>
                </a>
                <div className="puck-collection-view-header-main">
                  <h1 className="puck-section-title">
                    <span>{collectionMeta?.name || intl.formatMessage({ defaultMessage: 'Unnamed Collection' })}</span>
                  </h1>
                  {canDownloadCollection && (
                    <Button
                      color="primary"
                      className="puck-collection-view-download"
                      onClick={handleDownloadCollection}
                    >
                      <i className="far fa-download" /> {intl.formatMessage({ defaultMessage: 'Download all' })}
                    </Button>
                  )}
                </div>
              </div>
              {renderChildCollections()}
              {renderFiles()}
            </Container>
          </div>
          <PageFooter />
          <Actionbar filesTotal={filesTotal} collectionsTotal={childCollectionsTotal} />
        </div>
      )}
    </PageIdContext.Provider>
  );
};

PageCollectionView.propTypes = {
  onShowModal: PropTypes.func.isRequired,
  handleGetCollection: PropTypes.func.isRequired,
  selectLoadedFiles: PropTypes.func.isRequired,
  selectLoadedCollections: PropTypes.func.isRequired,
  resetSelectAll: PropTypes.func.isRequired,
  fixedModal: PropTypes.bool,
};

PageCollectionView.defaultProps = {
  fixedModal: false,
};

const mapDispatchToProps = (dispatch) => ({
  onShowModal: (modalName, params) => dispatch(showModal(modalName, params)),
  handleGetCollection: (collection) => dispatch(getCollection(collection)),
  selectLoadedFiles: files => dispatch((selectionDispatch, getState) => {
    if (getState().Files.selectedAllFiles && files.length > 0) {
      selectionDispatch(addSelectedFiles(files));
    }
  }),
  selectLoadedCollections: collections => dispatch((selectionDispatch, getState) => {
    if (getState().Collections.selectedAllCollections && collections.length > 0) {
      selectionDispatch(addSelectedCollections(collections));
    }
  }),
  resetSelectAll: () => {
    dispatch(setSelectedAllFiles(false));
    dispatch(setSelectedAllCollections(false));
  },
});

export default connect(null, mapDispatchToProps)(PageCollectionView);
